import { bytesToBase64Url } from '../../lib/pqc/codecs';
import { getPqcEngine, type SlhDsaVariant } from '../../lib/pqc/engine';
import {
  MAX_MESSAGE_BYTES,
  copyToClipboard,
  downloadText,
  formatBytes,
  formatDuration,
  getMessageBytes,
  setError,
  setStatus,
  updateByteCounter,
} from './pqc-shared';

const variantSelect = document.querySelector<HTMLSelectElement>('#slh-variant');
const generateButton = document.querySelector<HTMLButtonElement>('#slh-generate');
const pkField = document.querySelector<HTMLTextAreaElement>('#slh-pk');
const pkSize = document.querySelector<HTMLSpanElement>('#slh-pk-size');
const pkCopy = document.querySelector<HTMLButtonElement>('#slh-pk-copy');
const pkExport = document.querySelector<HTMLButtonElement>('#slh-pk-export');
const skField = document.querySelector<HTMLTextAreaElement>('#slh-sk');
const skSize = document.querySelector<HTMLSpanElement>('#slh-sk-size');
const sizePkSummary = document.querySelector<HTMLSpanElement>('#slh-size-pk');
const sizeSkSummary = document.querySelector<HTMLSpanElement>('#slh-size-sk');
const sizeSignatureSummary =
  document.querySelector<HTMLSpanElement>('#slh-size-signature');
const skCopy = document.querySelector<HTMLButtonElement>('#slh-sk-copy');
const skToggle = document.querySelector<HTMLButtonElement>('#slh-sk-toggle');
const exportAll = document.querySelector<HTMLButtonElement>('#slh-export-all');
const messageInput = document.querySelector<HTMLTextAreaElement>('#slh-message');
const messageSize = document.querySelector<HTMLSpanElement>('#slh-message-size');
const tamperButton = document.querySelector<HTMLButtonElement>('#slh-tamper');
const signButton = document.querySelector<HTMLButtonElement>('#slh-sign');
const resetButton = document.querySelector<HTMLButtonElement>('#slh-reset');
const signatureField = document.querySelector<HTMLTextAreaElement>('#slh-signature');
const signatureSize = document.querySelector<HTMLSpanElement>('#slh-signature-size');
const signatureCopy = document.querySelector<HTMLButtonElement>('#slh-signature-copy');
const verifyButton = document.querySelector<HTMLButtonElement>('#slh-verify');
const verifyResult = document.querySelector<HTMLParagraphElement>('#slh-verify-result');
const keygenTime = document.querySelector<HTMLSpanElement>('#slh-time-keygen');
const signTime = document.querySelector<HTMLSpanElement>('#slh-time-sign');
const verifyTime = document.querySelector<HTMLSpanElement>('#slh-time-verify');
const statusEl = document.querySelector<HTMLParagraphElement>('#slh-status');
const errorEl = document.querySelector<HTMLParagraphElement>('#slh-error');

if (
  !variantSelect ||
  !generateButton ||
  !pkField ||
  !pkSize ||
  !pkCopy ||
  !pkExport ||
  !skField ||
  !skSize ||
  !sizePkSummary ||
  !sizeSkSummary ||
  !sizeSignatureSummary ||
  !skCopy ||
  !skToggle ||
  !exportAll ||
  !messageInput ||
  !messageSize ||
  !tamperButton ||
  !signButton ||
  !resetButton ||
  !signatureField ||
  !signatureSize ||
  !signatureCopy ||
  !verifyButton ||
  !verifyResult ||
  !keygenTime ||
  !signTime ||
  !verifyTime ||
  !statusEl ||
  !errorEl
) {
  throw new Error('SLH-DSA demo elements not found.');
}

const enginePromise = getPqcEngine();

let publicKey: Uint8Array | null = null;
let secretKey: Uint8Array | null = null;
let signature: Uint8Array | null = null;

function zeroize(bytes: Uint8Array | null) {
  if (bytes) {
    bytes.fill(0);
  }
}

function setBusy(isBusy: boolean) {
  generateButton.disabled = isBusy;
  signButton.disabled = isBusy;
  verifyButton.disabled = isBusy;
}

function updateKeyFields() {
  if (publicKey) {
    pkField.value = bytesToBase64Url(publicKey);
    pkSize.textContent = formatBytes(publicKey.length);
    sizePkSummary.textContent = formatBytes(publicKey.length);
  } else {
    pkField.value = '';
    pkSize.textContent = '-';
    sizePkSummary.textContent = '-';
  }
  if (secretKey) {
    skField.value = bytesToBase64Url(secretKey);
    skSize.textContent = formatBytes(secretKey.length);
    sizeSkSummary.textContent = formatBytes(secretKey.length);
  } else {
    skField.value = '';
    skSize.textContent = '-';
    sizeSkSummary.textContent = '-';
  }
  skField.classList.add('hidden');
  skToggle.textContent = 'Reveal secret key';
}

function clearSignature() {
  zeroize(signature);
  signature = null;
  signatureField.value = '';
  signatureSize.textContent = '-';
  sizeSignatureSummary.textContent = '-';
  verifyResult.textContent = 'No signature yet.';
}

messageInput.addEventListener('input', () => {
  updateByteCounter(messageInput, messageSize, MAX_MESSAGE_BYTES);
});
updateByteCounter(messageInput, messageSize, MAX_MESSAGE_BYTES);

skToggle.addEventListener('click', () => {
  const isHidden = skField.classList.toggle('hidden');
  skToggle.textContent = isHidden ? 'Reveal secret key' : 'Hide secret key';
});

pkCopy.addEventListener('click', async () => {
  await copyToClipboard(pkField.value);
  setStatus(statusEl, 'Public key copied.');
});

skCopy.addEventListener('click', async () => {
  await copyToClipboard(skField.value);
  setStatus(statusEl, 'Secret key copied.');
});

signatureCopy.addEventListener('click', async () => {
  await copyToClipboard(signatureField.value);
  setStatus(statusEl, 'Signature copied.');
});

pkExport.addEventListener('click', () => {
  if (!publicKey) return;
  downloadText('slh-dsa-public-key.txt', pkField.value);
});

exportAll.addEventListener('click', () => {
  if (!publicKey || !secretKey) return;
  const payload = `Public key:\n${pkField.value}\n\nSecret key:\n${skField.value}`;
  downloadText('slh-dsa-keys-unsafe.txt', payload);
});

generateButton.addEventListener('click', async () => {
  setError(errorEl, '');
  setStatus(statusEl, 'Generating keypair...');
  setBusy(true);
  const variant = variantSelect.value as SlhDsaVariant;
  try {
    const start = performance.now();
    const engine = await enginePromise;
    const result = await engine.slhDsaKeygen(variant);
    keygenTime.textContent = formatDuration(performance.now() - start);
    zeroize(publicKey);
    zeroize(secretKey);
    publicKey = result.publicKey;
    secretKey = result.secretKey;
    updateKeyFields();
    clearSignature();
    setStatus(statusEl, 'Signing keypair ready.');
  } catch (error) {
    setError(errorEl, 'Unable to generate SLH-DSA keypair.');
    setStatus(statusEl, '');
  } finally {
    setBusy(false);
  }
});

tamperButton.addEventListener('click', () => {
  const text = messageInput.value;
  if (!text) {
    messageInput.value = 'tampered';
  } else {
    const first = text[0];
    const replacement = first === 'a' ? 'b' : 'a';
    messageInput.value = replacement + text.slice(1);
  }
  updateByteCounter(messageInput, messageSize, MAX_MESSAGE_BYTES);
  setStatus(statusEl, 'Message tampered.');
});

signButton.addEventListener('click', async () => {
  setError(errorEl, '');
  setStatus(statusEl, '');
  if (!secretKey) {
    setError(errorEl, 'Generate a signing keypair first.');
    return;
  }
  const messageBytes = getMessageBytes(messageInput.value);
  if (messageBytes.length > MAX_MESSAGE_BYTES) {
    setError(errorEl, 'Message is too large for the demo.');
    return;
  }
  setBusy(true);
  setStatus(statusEl, 'Signing message...');
  const variant = variantSelect.value as SlhDsaVariant;
  try {
    const start = performance.now();
    const engine = await enginePromise;
    const result = await engine.slhDsaSign(variant, secretKey, messageBytes);
    signTime.textContent = formatDuration(performance.now() - start);
    clearSignature();
    signature = result.signature;
    signatureField.value = bytesToBase64Url(signature);
    signatureSize.textContent = formatBytes(signature.length);
    sizeSignatureSummary.textContent = formatBytes(signature.length);
    verifyResult.textContent = 'Signature created. Ready to verify.';
    setStatus(statusEl, 'Signature created.');
  } catch (error) {
    setError(errorEl, 'Signing failed.');
    setStatus(statusEl, '');
  } finally {
    setBusy(false);
  }
});

verifyButton.addEventListener('click', async () => {
  setError(errorEl, '');
  setStatus(statusEl, '');
  if (!publicKey || !signature) {
    setError(errorEl, 'Generate keys and sign a message first.');
    return;
  }
  const messageBytes = getMessageBytes(messageInput.value);
  if (messageBytes.length > MAX_MESSAGE_BYTES) {
    setError(errorEl, 'Message is too large for the demo.');
    return;
  }
  setBusy(true);
  setStatus(statusEl, 'Verifying signature...');
  const variant = variantSelect.value as SlhDsaVariant;
  try {
    const start = performance.now();
    const engine = await enginePromise;
    const result = await engine.slhDsaVerify(variant, publicKey, messageBytes, signature);
    verifyTime.textContent = formatDuration(performance.now() - start);
    verifyResult.textContent = result.valid
      ? 'Valid signature for this message.'
      : 'Invalid signature for this message.';
    setStatus(statusEl, 'Verification complete.');
  } catch (error) {
    setError(errorEl, 'Verification failed.');
    setStatus(statusEl, '');
  } finally {
    setBusy(false);
  }
});

resetButton.addEventListener('click', () => {
  zeroize(publicKey);
  zeroize(secretKey);
  publicKey = null;
  secretKey = null;
  updateKeyFields();
  clearSignature();
  messageInput.value = '';
  updateByteCounter(messageInput, messageSize, MAX_MESSAGE_BYTES);
  keygenTime.textContent = '-';
  signTime.textContent = '-';
  verifyTime.textContent = '-';
  setStatus(statusEl, 'Reset complete.');
  setError(errorEl, '');
});
